import { useEffect, useState, useRef, useCallback } from "react";
import { LensPlatformClient, LensSDKException } from "lens-platform-sdk";
import { useAnalytics } from "src/hooks/useAnalytics";
import { useLensPlatformClient } from "./useLensPlatformClient";

type ActionResponse = Awaited<ReturnType<LensPlatformClient["user"]["setPrimaryEmail"]>>;

/**
 * Hook for setting email as the primary email.
 */
export const useSetPrimaryEmail = () => {
  const lensPlatformClient = useLensPlatformClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ActionResponse>();
  const [error, setError] = useState<LensSDKException>();
  const { track, trackError } = useAnalytics();
  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const setPrimaryEmail = useCallback(
    async (email: string) => {
      let lensSDKException: LensSDKException | undefined;

      try {
        if (mounted.current) {
          setError(undefined);
          setLoading(true);
        }
        const response = await lensPlatformClient.user.setPrimaryEmail(email);

        if (mounted.current) {
          track("Set Primary Email");
          setResponse(response);
        }
      } catch (exception) {
        if (exception instanceof LensSDKException) {
          trackError("Set Primary Email Failed");
          setError(exception);
          lensSDKException = exception;
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }

      return lensSDKException;
    },
    [lensPlatformClient.user, track, trackError],
  );

  return {
    setPrimaryEmail,
    error,
    response,
    loading,
  };
};
